import { ALLERGIES, Allergy } from './RecipeEnums';

// Wire catalogue: keep in sync with the recipe API and both mobile apps.
export const ALLERGEN_SUBTYPES: Partial<Record<Allergy, readonly string[]>> = {
  Gluten: ['wheat', 'rye', 'barley', 'oats'],
  'Tree Nuts': ['almond', 'hazelnut', 'walnut', 'cashew', 'pecan', 'brazil_nut', 'pistachio', 'macadamia'],
};

export interface AllergenDeclaration {
  presence: 'contains' | 'absent' | 'unknown';
  subtypes?: string[];
  subtypesComplete?: boolean;
}

export interface AllergenDraft {
  version: 1;
  groups: Partial<Record<Allergy, AllergenDeclaration>>;
  review: { status: 'unreviewed' | 'reviewed' | 'needs_review'; reviewedAt?: string };
}

export interface AllergenDetailsV1 extends AllergenDraft {
  revision: string;
}

export function emptyAllergenDraft(): AllergenDraft {
  return { version: 1, groups: {}, review: { status: 'unreviewed' } };
}

export function getUnmappedLegacyAllergens(allergies: readonly string[] = []): string[] {
  return Array.from(new Set(allergies.filter(allergy => !ALLERGIES.includes(allergy as Allergy))));
}

export function isSupportedAllergenDetails(value: unknown): value is AllergenDetailsV1 {
  if (!value || typeof value !== 'object') return false;
  const details = value as AllergenDetailsV1;
  if (details.version !== 1 || typeof details.revision !== 'string' || !details.revision ||
      !details.groups || typeof details.groups !== 'object' || Array.isArray(details.groups) ||
      !['unreviewed', 'reviewed', 'needs_review'].includes(details.review?.status)) return false;
  return Object.entries(details.groups).every(([key, group]) => {
    if (!ALLERGIES.includes(key as Allergy) || !group || typeof group !== 'object' ||
        !['contains', 'absent', 'unknown'].includes(group.presence)) return false;
    if (group.subtypesComplete !== undefined && typeof group.subtypesComplete !== 'boolean') return false;
    if (group.subtypes !== undefined && (!Array.isArray(group.subtypes) ||
        !ALLERGEN_SUBTYPES[key as Allergy] || group.presence !== 'contains' ||
        !group.subtypes.every(value => ALLERGEN_SUBTYPES[key as Allergy]?.includes(value)))) return false;
    return true;
  });
}

export function draftFromRecipe(recipe?: { allergies: string[]; allergenDetails?: AllergenDetailsV1 | null }): AllergenDraft {
  if (isSupportedAllergenDetails(recipe?.allergenDetails)) {
    const { revision, ...draft } = recipe!.allergenDetails!;
    return JSON.parse(JSON.stringify(draft));
  }
  const draft = emptyAllergenDraft();
  for (const group of ALLERGIES) {
    if (recipe?.allergies.includes(group)) draft.groups[group] = { presence: 'contains' };
  }
  return draft;
}

export function canReviewAllergens(draft: AllergenDraft): boolean {
  return ALLERGIES.every(group => {
    const declaration = draft.groups[group];
    if (!declaration || declaration.presence === 'unknown') return false;
    return declaration.presence !== 'contains' || !ALLERGEN_SUBTYPES[group] ||
      (declaration.subtypesComplete === true && !!declaration.subtypes?.length &&
        declaration.subtypes.every(value => ALLERGEN_SUBTYPES[group]!.includes(value)));
  });
}

export function changeAllergen(draft: AllergenDraft, group: Allergy, declaration: AllergenDeclaration): AllergenDraft {
  return {
    version: 1,
    groups: { ...draft.groups, [group]: declaration },
    review: { status: draft.review.status === 'unreviewed' ? 'unreviewed' : 'needs_review' },
  };
}
